/**
 * CONSTANTES DE LA APLICACIÓN - HANAMIPOS FRONTEND
 *
 * Este archivo contiene todas las constantes usadas en la aplicación.
 * Centralizar constantes hace el código más mantenible y consistente.
 *
 * ORGANIZACIÓN:
 * - Roles de usuario
 * - Métodos de pago
 * - Estados de venta
 * - Configuración del POS
 * - URLs de la API
 * - Mensajes de error/éxito
 */

/**
 * ROLES DE USUARIO
 *
 * Estos roles deben coincidir exactamente con los del backend Django
 */
export const USER_ROLES = {
  ADMIN: 'admin',
  SUPERVISOR: 'supervisor',
  CAJERO: 'cajero'
}

// Mapeo de roles a nombres legibles
export const ROLE_LABELS = {
  [USER_ROLES.ADMIN]: 'Administrador',
  [USER_ROLES.SUPERVISOR]: 'Supervisor',
  [USER_ROLES.CAJERO]: 'Cajero'
}

// Permisos por rol
export const ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: [
    'pos:use',
    'products:manage',
    'reports:view',
    'users:manage',
    'settings:manage',
    'sessions:manage'
  ],
  [USER_ROLES.SUPERVISOR]: [
    'pos:use',
    'products:toggle',
    'reports:view',
    'sessions:view'
  ],
  [USER_ROLES.CAJERO]: [
    'pos:use'
  ]
}

/**
 * MÉTODOS DE PAGO
 *
 * Métodos de pago soportados por el POS
 */
export const PAYMENT_METHODS = {
  EFECTIVO: 'efectivo',
  TARJETA: 'tarjeta',
  TRANSFERENCIA: 'transferencia'
}

// Labels para métodos de pago
export const PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHODS.EFECTIVO]: 'Efectivo',
  [PAYMENT_METHODS.TARJETA]: 'Tarjeta',
  [PAYMENT_METHODS.TRANSFERENCIA]: 'Transferencia'
}

// Íconos para métodos de pago
export const PAYMENT_METHOD_ICONS = {
  [PAYMENT_METHODS.EFECTIVO]: '💵',
  [PAYMENT_METHODS.TARJETA]: '💳',
  [PAYMENT_METHODS.TRANSFERENCIA]: '📱'
}

/**
 * ESTADOS DE VENTA
 *
 * Estados posibles de una venta en el sistema
 */
export const SALE_STATES = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded'
}

// Labels para estados de venta
export const SALE_STATE_LABELS = {
  [SALE_STATES.PENDING]: 'Pendiente',
  [SALE_STATES.COMPLETED]: 'Completada',
  [SALE_STATES.CANCELLED]: 'Cancelada',
  [SALE_STATES.REFUNDED]: 'Reembolsada'
}

/**
 * CONFIGURACIÓN DEL POS
 *
 * Constantes específicas para el funcionamiento del POS
 */

// IVA México (16%)
export const TAX_RATE = 0.16

// Moneda por defecto
export const DEFAULT_CURRENCY = 'MXN'

// Símbolo de moneda
export const CURRENCY_SYMBOL = '$'

// Longitud del PIN de usuarios
export const PIN_LENGTH = parseInt(import.meta.env.VITE_PIN_LENGTH) || 4

// Límites del sistema
export const LIMITS = {
  MAX_SALE_AMOUNT: 99999.99,
  MIN_SALE_AMOUNT: 0.01,
  MAX_QUANTITY_PER_ITEM: 999,
  MAX_ITEMS_PER_SALE: 50,
  MAX_DAILY_ORDER_NUMBER: 999
}

// Configuración de sesiones de trabajo
export const WORK_SESSION = {
  MIN_STARTING_CASH: 0,
  MAX_STARTING_CASH: 50000,
  AUTO_CLOSE_HOURS: 12 // Cerrar automáticamente después de 12 horas
}

/**
 * ENDPOINTS DE LA API
 *
 * URLs relativas para diferentes endpoints
 */
export const API_ENDPOINTS = {
  // Autenticación
  AUTH: {
    PIN_LOGIN: '/auth/pin-login/',
    LOGOUT: '/auth/logout/',
    TOKEN_REFRESH: '/auth/token/refresh/',
    CURRENT_SESSION: '/auth/work-sessions/current_session/',
    START_SESSION: (userId) => `/auth/users/${userId}/start_session/`,
    END_SESSION: (userId) => `/auth/users/${userId}/end_session/`
  },

  // POS
  POS: {
    QUICK_SALE: '/pos/quick-sale/',
    OPEN_SALE: '/pos/sales/open/',
    COMPLETE_OPEN_SALE_PAYMENT: (id) => `/pos/sales/${id}/complete_payment/`,
    DASHBOARD_STATS: '/pos/dashboard-stats/',
    ALERTS: '/pos/alerts/',
    SALES_TODAY: '/pos/sales/today/'
  },

  // Productos
  PRODUCTS: {
    CATEGORIES: '/products/categories/',
    POS_CATALOG: '/products/categories/pos_catalog/',
    PRODUCTS: '/products/products/',
    TOGGLE_AVAILABILITY: (id) => `/products/products/${id}/toggle_availability/`
  },

  // Reportes
  REPORTS: {
    DAILY_SUMMARY: '/reports/daily-summary/',
    SALES_BY_PERIOD: '/reports/sales-by-period/',
    TOP_PRODUCTS: '/reports/top-products/'
  }
}

/**
 * MENSAJES DE LA APLICACIÓN
 *
 * Mensajes de error, éxito e información
 */

// Mensajes de error
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Error de conexión. Verifica tu internet.',
  INVALID_PIN: 'PIN incorrecto. Intenta nuevamente.',
  PIN_TOO_SHORT: `El PIN debe tener ${PIN_LENGTH} dígitos.`,
  SESSION_EXPIRED: 'Tu sesión ha expirado. Inicia sesión nuevamente.',
  INSUFFICIENT_PERMISSIONS: 'No tienes permisos para realizar esta acción.',
  INVALID_AMOUNT: 'La cantidad ingresada no es válida.',
  INSUFFICIENT_STOCK: 'Stock insuficiente para este producto.',
  SALE_FAILED: 'Error al procesar la venta. Intenta nuevamente.',
  GENERIC_ERROR: 'Ha ocurrido un error inesperado.'
}

// Mensajes de éxito
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Inicio de sesión exitoso.',
  SALE_COMPLETED: 'Venta completada exitosamente.',
  SESSION_STARTED: 'Sesión de trabajo iniciada.',
  SESSION_ENDED: 'Sesión de trabajo finalizada.',
  PRODUCT_UPDATED: 'Producto actualizado correctamente.',
  LOGOUT_SUCCESS: 'Sesión cerrada correctamente.'
}

// Mensajes informativos
export const INFO_MESSAGES = {
  LOADING: 'Cargando...',
  PROCESSING_SALE: 'Procesando venta...',
  SAVING: 'Guardando...',
  NO_PRODUCTS: 'No hay productos disponibles.',
  EMPTY_CART: 'El carrito está vacío.',
  NO_SALES_TODAY: 'No hay ventas registradas hoy.'
}

/**
 * CONFIGURACIÓN DE INTERFAZ
 *
 * Constantes para la interfaz de usuario
 */

// Tamaños de elementos touch-friendly
export const TOUCH_SIZES = {
  MIN_BUTTON_SIZE: 44, // píxeles
  MIN_TAP_TARGET: 44,
  CARD_PADDING: 16,
  MODAL_PADDING: 24
}

// Breakpoints para responsive design
export const BREAKPOINTS = {
  SM: 640,   // Móviles grandes
  MD: 768,   // Tablets
  LG: 1024,  // Tablets grandes / Laptops
  XL: 1280,  // Desktop
  XXL: 1536  // Desktop grande
}

// Colores del tema
export const THEME_COLORS = {
  PRIMARY: '#059669',     // Verde esmeralda
  SUCCESS: '#10b981',     // Verde éxito
  WARNING: '#f59e0b',     // Amarillo advertencia
  ERROR: '#ef4444',       // Rojo error
  INFO: '#3b82f6',        // Azul información
  NEUTRAL: '#6b7280'      // Gris neutral
}

/**
 * CONFIGURACIÓN DE NOTIFICACIONES
 *
 * Tipos y configuración para notificaciones/alertas
 */
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
}

// Duración de notificaciones (en milisegundos)
export const NOTIFICATION_DURATION = {
  [NOTIFICATION_TYPES.SUCCESS]: 3000,
  [NOTIFICATION_TYPES.ERROR]: 5000,
  [NOTIFICATION_TYPES.WARNING]: 4000,
  [NOTIFICATION_TYPES.INFO]: 3000
}

/**
 * CONFIGURACIÓN DE ALMACENAMIENTO LOCAL
 *
 * Keys para localStorage y sessionStorage
 */
export const STORAGE_KEYS = {
  ACCESS_TOKEN: import.meta.env.VITE_JWT_ACCESS_TOKEN_KEY || 'access_token',
  REFRESH_TOKEN: import.meta.env.VITE_JWT_REFRESH_TOKEN_KEY || 'refresh_token',
  USER_DATA: 'user_data',
  CART_DATA: 'cart_data',
  PREFERENCES: 'user_preferences',
  LAST_SESSION: 'last_session_data'
}

/**
 * CONFIGURACIÓN DE VALIDACIÓN
 *
 * Reglas de validación para formularios
 */
export const VALIDATION_RULES = {
  PIN: {
    length: PIN_LENGTH,
    pattern: /^\d+$/,
    message: `El PIN debe tener ${PIN_LENGTH} dígitos numéricos`
  },
  AMOUNT: {
    min: LIMITS.MIN_SALE_AMOUNT,
    max: LIMITS.MAX_SALE_AMOUNT,
    pattern: /^\d+(\.\d{1,2})?$/,
    message: 'Ingresa una cantidad válida (máximo 2 decimales)'
  },
  QUANTITY: {
    min: 1,
    max: LIMITS.MAX_QUANTITY_PER_ITEM,
    pattern: /^\d+$/,
    message: 'La cantidad debe ser un número entero positivo'
  }
}

/**
 * CONFIGURACIÓN DE FORMATO
 *
 * Configuración para formateo de números, fechas, etc.
 */
export const FORMAT_OPTIONS = {
  CURRENCY: {
    style: 'currency',
    currency: DEFAULT_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  },
  PERCENTAGE: {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  },
  DATE: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  },
  TIME: {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  },
  DATETIME: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }
}

/**
 * CONFIGURACIÓN DE IMPRESIÓN TÉRMICA
 *
 * Configuración para impresoras térmicas ESC/POS vía Bluetooth
 */
export const THERMAL_PRINTER_CONFIG = {
  // UUIDs de servicios Bluetooth comunes en impresoras térmicas
  SERVICE_UUIDS: [
    '000018f0-0000-1000-8000-00805f9b34fb', // UUID genérico de impresión
    'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // UUID común en impresoras chinas
    '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Bluetooth SPP (Serial Port Profile)
  ],

  // UUIDs de características comunes
  CHARACTERISTIC_UUIDS: [
    '00002af1-0000-1000-8000-00805f9b34fb', // UUID genérico de escritura
    '49535343-8841-43f4-a8d4-ecbe34729bb3', // UUID de escritura SPP
  ],

  // Anchos de papel soportados (en mm)
  PAPER_WIDTHS: {
    SMALL: 58,
    MEDIUM: 80,
    LARGE: 110
  },

  // Caracteres por línea según ancho de papel
  CHARS_PER_LINE: {
    58: 32,  // 58mm ≈ 32 caracteres
    80: 48,  // 80mm ≈ 48 caracteres
    110: 64  // 110mm ≈ 64 caracteres
  },

  // Tamaño de chunk para transmisión Bluetooth (bytes)
  BLUETOOTH_CHUNK_SIZE: 20,

  // Delay entre chunks (ms)
  BLUETOOTH_CHUNK_DELAY: 10,

  // Timeout para conexión (ms)
  CONNECTION_TIMEOUT: 10000,

  // Timeout para impresión (ms)
  PRINT_TIMEOUT: 30000
}

// Métodos de impresión disponibles
export const PRINT_METHODS = {
  BROWSER: 'browser',      // Impresión vía window.print()
  THERMAL: 'thermal',      // Impresión térmica vía Bluetooth
  PREVIEW: 'preview'       // Vista previa sin imprimir
}

// Labels para métodos de impresión
export const PRINT_METHOD_LABELS = {
  [PRINT_METHODS.BROWSER]: 'Impresora Estándar',
  [PRINT_METHODS.THERMAL]: 'Impresora Térmica Bluetooth',
  [PRINT_METHODS.PREVIEW]: 'Vista Previa'
}

/**
 * UTILIDADES EXPORTADAS
 */

// Verificar si un usuario tiene un permiso específico
export const hasPermission = (userRole, permission) => {
  return ROLE_PERMISSIONS[userRole]?.includes(permission) || false
}

// Verificar si un rol es admin
export const isAdmin = (userRole) => {
  return userRole === USER_ROLES.ADMIN
}

// Verificar si un rol puede usar el POS
export const canUsePOS = (userRole) => {
  return hasPermission(userRole, 'pos:use')
}

// Obtener el label de un rol
export const getRoleLabel = (userRole) => {
  return ROLE_LABELS[userRole] || userRole
}

// Obtener el label de un método de pago
export const getPaymentMethodLabel = (method) => {
  return PAYMENT_METHOD_LABELS[method] || method
}